import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ForumStyles.css';

const ForumList = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    const showToastMsg = (message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
    };

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = () => {
        axios.get('https://oceansense-backend.onrender.com/api/forum/posts')
            .then(response => {
                setPosts(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching posts:', error);
                setLoading(false);
            });
    };

    const addPost = (newPost) => {
        setPosts([newPost, ...posts]);
        showToastMsg('Post created successfully!', 'success');
        setShowForm(false);
    };

    const deletePost = (id) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            axios.delete(`https://oceansense-backend.onrender.com/api/forum/delete/${id}`)
                .then(() => {
                    setPosts(posts.filter(post => post.id !== id));
                    showToastMsg('Post deleted.', 'info');
                })
                .catch(error => {
                    console.error('Error deleting post:', error);
                    showToastMsg('Failed to delete post.', 'error');
                });
        }
    };

    return (
        <div className="forum-page">
            {/* Toast */}
            {toast.show && (
                <div className={`forum-toast ${toast.type}`}>
                    <span>{toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}</span>
                    {toast.message}
                </div>
            )}

            <div className="forum-container">
                <div className="forum-header">
                    <div>
                        <span className="forum-badge">💬 Community</span>
                        <h1>Community Forum</h1>
                        <p className="forum-subtitle">Share ideas, ask questions, and connect with fellow ocean advocates.</p>
                    </div>
                    <button className="new-post-btn" onClick={() => setShowForm(!showForm)}>
                        {showForm ? '✕ Cancel' : '✚ New Post'}
                    </button>
                </div>

                {/* Create Form (toggleable) */}
                {showForm && <CreateForumPost addPost={addPost} showToast={showToastMsg} />}

                {/* Posts */}
                {loading ? (
                    <div className="forum-loading">
                        <div className="forum-spinner"></div>
                        <p>Loading discussions...</p>
                    </div>
                ) : posts.length === 0 ? (
                    <div className="forum-empty">
                        <span className="empty-icon">🌊</span>
                        <h3>No discussions yet</h3>
                        <p>Be the first to start a conversation about ocean conservation!</p>
                    </div>
                ) : (
                    <div className="posts-list">
                        {posts.map(post => (
                            <ForumPost key={post.id} post={post} deletePost={deletePost} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const ForumPost = ({ post, deletePost }) => {
    return (
        <div className="post-card">
            <div className="post-avatar">{post.author?.charAt(0).toUpperCase() || '?'}</div>
            <div className="post-body">
                <div className="post-meta">
                    <span className="post-author">{post.author}</span>
                    <span className="post-date">{new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <h3 className="post-title">{post.title}</h3>
                <p className="post-content">{post.content}</p>
            </div>
            <button onClick={() => deletePost(post.id)} className="delete-button" title="Delete post">
                🗑️
            </button>
        </div>
    );
};

const CreateForumPost = ({ addPost, showToast }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [author, setAuthor] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!title || !content || !author) {
            showToast('Please fill in all fields.', 'warning');
            return;
        }

        setSubmitting(true);
        const newPost = { title, content, author };

        axios.post('https://oceansense-backend.onrender.com/api/forum/create', newPost)
            .then(response => {
                addPost(response.data);
                setTitle('');
                setContent('');
                setAuthor('');
                setSubmitting(false);
            })
            .catch(error => {
                console.error('Error creating post:', error);
                showToast('Failed to create post.', 'error');
                setSubmitting(false);
            });
    };

    return (
        <div className="create-form-card">
            <h2>✍️ Create a New Post</h2>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Your Name</label>
                    <input 
                        type="text" 
                        value={author} 
                        onChange={(e) => setAuthor(e.target.value)} 
                        placeholder="Enter your name" 
                        required 
                    />
                </div>
                <div className="form-group">
                    <label>Title</label>
                    <input 
                        type="text" 
                        value={title} 
                        onChange={(e) => setTitle(e.target.value)} 
                        placeholder="Give your post a title" 
                        required 
                    />
                </div>
                <div className="form-group">
                    <label>Content</label>
                    <textarea 
                        value={content} 
                        onChange={(e) => setContent(e.target.value)} 
                        placeholder="Share your thoughts about ocean conservation..." 
                        required 
                    ></textarea>
                    <span className="char-count">{content.length} characters</span>
                </div>
                <button type="submit" className="submit-post-btn" disabled={submitting}>
                    {submitting ? 'Posting...' : 'Publish Post'}
                </button>
            </form>
        </div>
    );
};

export default ForumList;
